import type { FileRef, Translation } from '@/lib/api/common'

/** How the storefront may be served. A tenant can offer any combination. */
export const ShippingMethod = {
  DiningIn: 'dining_in',
  PickUp: 'pickup',
  Delivery: 'delivery',
} as const

export type ShippingMethod = (typeof ShippingMethod)[keyof typeof ShippingMethod]

export const shippingMethodLabels: Record<ShippingMethod, string> = {
  [ShippingMethod.DiningIn]: 'Dining in',
  [ShippingMethod.PickUp]: 'Pick up',
  [ShippingMethod.Delivery]: 'Delivery',
}

/**
 * The tenant's settings blob.
 *
 * Every branch of it is optional on the way down: the column is free-form JSON
 * and a tenant created before a section existed simply does not carry it.
 */
export interface TenantConfiguration {
  theme?: { primaryColor?: string; secondaryColor?: string }
  setting?: { telegramId?: string }
  shipping?: { shippingMethod?: ShippingMethod[] }
  admin?: { allowProductSync?: boolean }
}

/** The business itself, as `GET /v1/merchant/tenants` returns it. */
export interface Tenant {
  id: string
  name: Translation
  description: Translation | null
  address: Translation | null
  configuration: TenantConfiguration | null
  logo: FileRef | null
  cover: FileRef | null
  enabled: boolean
  /** Set by NexQR staff, not by the merchant. Shown, never edited here. */
  adminEnabled: boolean
  rating: number
  contactNumber: string | null
}

/**
 * What `PUT /v1/merchant/tenants` takes.
 *
 * `configuration` is replaced wholesale rather than merged, so the form has to
 * send back the sections it did not touch.
 */
export interface UpdateTenantBody {
  name?: Translation
  description?: Translation
  address?: Translation
  logoId?: string
  coverId?: string
  contactNumber?: string
  messengerLink?: string
  telegramLink?: string
  configuration?: TenantConfiguration
}

/** An outlet. Branches are what products, staff, tables and hours hang off. */
export interface TenantBranch {
  id: string
  name: Translation
  address: Translation | null
  enabled: boolean
  latitude: number
  longitude: number
  /** `HH:mm`, and the branch's own trading window rather than its open hours. */
  openHour: string | null
  closeHour: string | null
}

/** One body for create and update — the endpoints take the same shape. */
export interface SaveBranchBody {
  name: Translation
  address: Translation
  latitude: number
  longitude: number
  openHour?: string
  closeHour?: string
}
